// rawLink.ts — link ảnh gốc mở QUA BE để ghi sổ (anti-fraud M4).
//
// Ảnh gốc là link ngoài (Drive/Dropbox) dán vào đơn. FE không render link
// trần nữa: href trỏ về GET /orders/:id/raw-link?i=N — BE ghi một dòng vào
// sổ raw_link_views rồi 302 về link thật. Auth mặc định là cookie session
// (`session_id`, SameSite=Lax) nên thẻ <a target="_blank"> vẫn mang phiên
// khi mở tab mới — không cần fetch tay.
//
// Hợp đồng thứ tự với BE (backend/internal/core/order/rawlink_handler.go):
// - nguồn "order" (orderRawLinks): mảng `raw_photo_links` đã lọc phần tử
//   rỗng, rồi cột legacy `raw_photo_link` (nếu có) vào CUỐI;
// - nguồn "pipeline" (?src=pipeline — vá 5B): mảng `cskh_pipeline.raw_links`
//   cùng phép lọc (cleanRawLinksJSON).
// Đổi một bên là phải đổi bên kia.

import { apiBaseUrl } from "./client";

export type RawLinkSource = {
  rawPhotoLink?: string | null;
  rawPhotoLinks?: unknown;
};

export type RawLinkEntry = {
  /** Link thật (FE vẫn nhận qua API — chỉ không đem render làm href/text). */
  url: string;
  /** Vị trí trong danh sách BE ghép — tham số ?i= của route raw-link. */
  i: number;
};

/**
 * Danh sách link gốc của đơn theo ĐÚNG thứ tự BE ghép. Legacy trùng với một
 * phần tử trong mảng thì ẩn dòng đó (index các dòng còn lại không đổi vì
 * legacy luôn nằm cuối).
 */
export function orderRawLinkEntries(order: RawLinkSource): RawLinkEntry[] {
  const out: RawLinkEntry[] = [];
  const seen = new Set<string>();
  if (Array.isArray(order.rawPhotoLinks)) {
    for (const value of order.rawPhotoLinks) {
      if (typeof value !== "string") continue;
      const url = value.trim();
      if (!url) continue;
      out.push({ url, i: out.length });
      seen.add(url);
    }
  }
  const legacy = order.rawPhotoLink?.trim();
  if (legacy && !seen.has(legacy)) out.push({ url: legacy, i: out.length });
  return out;
}

/** Href mở link gốc thứ i của đơn qua BE (302 sau khi ghi sổ). */
export function rawLinkHref(orderId: string, i: number): string {
  return `${apiBaseUrl}/orders/${encodeURIComponent(orderId)}/raw-link?i=${i}`;
}

/**
 * Href BE cho một URL cụ thể — null khi URL chưa được lưu vào đơn (nháp
 * trong editor): chưa vào kho thì chưa có gì để ghi sổ, caller tự quyết
 * (thường giữ href trần tạm cho tới khi lưu xong).
 */
export function rawLinkHrefFor(
  orderId: string,
  order: RawLinkSource,
  url: string,
): string | null {
  const target = url.trim();
  const entry = orderRawLinkEntries(order).find((e) => e.url === target);
  return entry ? rawLinkHref(orderId, entry.i) : null;
}

/**
 * Danh sách link gốc CSKH dán vào pipeline (`pipeline.rawLinks`) theo ĐÚNG
 * phép lọc của BE: chỉ giữ string, trim, bỏ rỗng — index tính SAU lọc.
 */
export function pipelineRawLinkEntries(rawLinks: unknown): RawLinkEntry[] {
  const out: RawLinkEntry[] = [];
  if (!Array.isArray(rawLinks)) return out;
  for (const value of rawLinks) {
    if (typeof value !== "string") continue;
    const url = value.trim();
    if (!url) continue;
    out.push({ url, i: out.length });
  }
  return out;
}

/** Href mở link gốc thứ i trong pipeline CSKH của đơn qua BE (ghi sổ source=pipeline). */
export function rawLinkPipelineHref(orderId: string, i: number): string {
  return `${rawLinkHref(orderId, i)}&src=pipeline`;
}

/**
 * Href BE cho một URL trong danh sách pipeline ĐÃ LƯU — null khi URL chưa
 * lưu (nháp trong editor), caller giữ href trần tạm tới khi lưu xong (cùng
 * quy ước rawLinkHrefFor).
 */
export function rawLinkPipelineHrefFor(
  orderId: string,
  savedRawLinks: unknown,
  url: string,
): string | null {
  const target = url.trim();
  const entry = pipelineRawLinkEntries(savedRawLinks).find((e) => e.url === target);
  return entry ? rawLinkPipelineHref(orderId, entry.i) : null;
}

/** Nhãn rút gọn cho link gốc — chỉ hostname, không lộ URL thật. */
export function rawLinkLabel(url: string): string {
  try {
    // "Drive: ghi chú" parse được thành scheme drive: với hostname rỗng —
    // vẫn phải rơi về nhãn chung.
    return new URL(url).hostname || "liên kết";
  } catch {
    return "liên kết";
  }
}
